import { Dispatch } from '@reduxjs/toolkit';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import {
  GraphQueryTemplate,
  GraphQueryTemplateId,
  GraphQueryTemplateStatus,
  sanitizeGraphQueryTemplate,
} from 'dashboard/models/graph-query-template';
import { Filters, setGraphQueryTemplates } from 'dashboard/redux/actions/graph-query-templates';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';

export function loadGraphQueryTemplates(
  filters: Filters = {},
  sortParams?: SortParams,
  abortSignal?: AbortSignal,
): DashboardThunkAction<Promise<void>> {
  const options: { [key: string]: any } = {
    filter: { status: filters?.status },
    ...(sortParams && { sort: `${sortParams.shouldSortDescending ? '-' : ''}${sortParams.sort}` }),
    signal: abortSignal,
  };

  return async (dispatch: Dispatch) => {
    const { data: graphQueryTemplates } =
      await SessionClientDashboard.listResource<GraphQueryTemplate>(
        ApiEndpoint.GraphQueryTemplate,
        options,
      );

    dispatch(setGraphQueryTemplates(graphQueryTemplates.map(sanitizeGraphQueryTemplate)));
  };
}

export function archiveGraphQueryTemplate(
  graphQueryTemplateId: GraphQueryTemplateId,
): DashboardThunkAction {
  return async (dispatch: Dispatch, getState) => {
    const { data: deletedGraphQueryTemplate } =
      await SessionClientDashboard.deleteResource<GraphQueryTemplate>(
        ApiEndpoint.GraphQueryTemplate,
        graphQueryTemplateId,
      );

    const templates: GraphQueryTemplate[] = getState().graphQueryTemplates.graphQueryTemplates;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const shouldKeep = getState().graphQueryTemplates.filters.status.includes(
      GraphQueryTemplateStatus.Archived,
    );

    dispatch(
      setGraphQueryTemplates(
        shouldKeep
          ? templates.map((template) =>
              template.id === deletedGraphQueryTemplate.id
                ? sanitizeGraphQueryTemplate(deletedGraphQueryTemplate)
                : template,
            )
          : templates.filter((template) => template.id !== deletedGraphQueryTemplate.id),
      ),
    );
  };
}

export function restoreGraphQueryTemplate(
  graphQueryTemplateId: GraphQueryTemplateId,
): DashboardThunkAction {
  return async (dispatch: Dispatch, getState) => {
    const { data: restoredGraphQueryTemplate } = await SessionClientDashboard.postResourceAction<
      GraphQueryTemplate,
      {
        type: ApiModel.GraphQueryTemplate;
        id: string;
      }
    >(
      {
        type: ApiModel.GraphQueryTemplate,
        id: graphQueryTemplateId,
      },
      'restore',
    );

    const templates: GraphQueryTemplate[] = getState().graphQueryTemplates.graphQueryTemplates;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const shouldKeep = getState().graphQueryTemplates.filters.status.includes(
      GraphQueryTemplateStatus.Active,
    );

    dispatch(
      setGraphQueryTemplates(
        shouldKeep
          ? templates.map((template) =>
              template.id === restoredGraphQueryTemplate.id
                ? sanitizeGraphQueryTemplate(restoredGraphQueryTemplate)
                : template,
            )
          : templates.filter((template) => template.id !== restoredGraphQueryTemplate.id),
      ),
    );
  };
}
